import {
  financialEffectMinMaxThresholds,
  financialMinMaxThresholds,
  impactMinMaxThresholds,
} from '@upright/common/model/input/materiality-assessment/default-thresholds';
import {
  FinancialDriver,
  FinancialItem,
  FinancialMaterialityLevel,
  GenericLevel,
  MaterialityActualness,
  MaterialityTimeHorizon,
  MaterialityType,
  MaterialityValence,
  MaterialityValueChain,
} from '@upright/common/model/input/materiality-assessment/types';
import {
  convertLegacyMatterId,
  friendlySustainabilityMatterTitle,
  getAssessableLeafMattersUnder,
  isAssessableLeafMatter,
  isSustainabilityMatterId,
} from '@upright/common/model/input/materiality-assessment/matters';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import {
  CsrdResultTailoring,
  TailoringType,
  UpdatePatchFinancialEffectTailoring,
  UpdatePatchFinancialTailoring,
} from '@upright/common/model/input/materiality-assessment/types-tailoring';
import { driverToValidFinancialItems } from '@upright/common/model/output/materiality/csrd-company-results/result-tailoring/utils';
import { z } from 'zod';
import { TailoringEvent } from './types';

export type CreateCsrdResultTailoringResponse = {
  tailoringId: number;
};

export type DeleteCsrdResultTailoringResponse = boolean;

export type SupersedeCsrdResultTailoringResponse = {
  createdTailoringId: number;
  removeTailoringId: number;
};

export type ResetAllCsrdResultTailoringsRequestParams = {
  companyId: string;
  variantId: string;
};

export type ResetAllCsrdResultTailoringsResponse = boolean;

export type TailoringMetadata = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  createdByUser: TailoringUser;
  updatedByUser: TailoringUser;
};

export type TailoringUser = {
  id: number;
  username: string;
  firstName: string | undefined;
  lastName: string | undefined;
};

export type CsrdResultTailoringWithMetadata = CsrdResultTailoring & TailoringMetadata;

export type GetCsrdResultTailoringResponse = CsrdResultTailoringWithMetadata;
export type ListCsrdResultTailoringResponse = CsrdResultTailoringWithMetadata[];

export type ListCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
};

export type CreateCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
};

export type GetCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
  tailoringId: number;
};

export type DeleteCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
  tailoringId: number;
};

export type SupersedeCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
};

export type UpdateCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
  tailoringId: number;
};

export type UpdateCsrdResultTailoringResponse = boolean;

export type GetCsrdResultTailoringEventsRequestParams = {
  companyId: string;
  variantId: string;
};

export type GetCsrdResultTailoringEventsResponse = TailoringEvent[];

const reasoningValidator = z.string().min(1, 'Please provide a reasoning with length of more than 1 character');
const descriptionValidator = z.string().min(1, 'Please provide a description with length of more than 1 character');

/** Keeps the rejection message bounded when the offending id sits high up the hierarchy. */
const MAX_SUGGESTED_MATTERS = 12;

function describeMatter(matterId: SustainabilityMatterId): string {
  return `${matterId} (${friendlySustainabilityMatterTitle(matterId)})`;
}

/**
 * Why `matterId` cannot carry a tailoring, or `undefined` when it can. Single source for the
 * rejection across both boundaries a tailoring's matter crosses — this file's write schemas and the
 * `propose_tailoring_batch` chat tool's per-item label — so the API and the agent are never told
 * different things about the same id.
 *
 * A tailoring must target a matter the assessment actually has a row for, and
 * `SustainabilityMatterId` is wider than that: it also contains the ESRS topic headings above the
 * assessable matters (e.g. `G1-bco`, Business conduct), the Upright matters that duplicate an ESRS
 * leaf, and retired legacy ids. A tailoring on any of those is stored successfully and then renders
 * nowhere, because every DMA surface filters its rows through `isRelevantLeafMatter` — so the write
 * looks like a success to everyone involved while the user never sees their change (PLT-6774).
 *
 * The message names the matters that CAN carry the tailoring rather than remapping to one of them:
 * which leaf a heading was meant to stand for is a guess, and guessing wrong files the user's impact
 * under the wrong matter just as silently.
 */
export function nonAssessableMatterIdMessage(matterId: string): string | undefined {
  if (!isSustainabilityMatterId(matterId)) {
    return `${matterId} is not a sustainability matter id.`;
  }
  if (isAssessableLeafMatter(matterId)) return undefined;

  // A retired id has a documented successor, so name it rather than dead-ending. This is the case a
  // prior DMA's tailorings hit on re-import (`list_importable_tailorings`): the row was stored before
  // the matter was retired, and recreating it under the old id would render nowhere just as a heading
  // would. Assessable ids never reach here, so the mitigation/adaptation swap in `convertLegacyMatterId`
  // is out of play.
  const successor = convertLegacyMatterId(matterId);
  if (successor !== matterId && isAssessableLeafMatter(successor)) {
    return (
      `${matterId} is a retired sustainability matter id and can no longer carry a tailoring. It was ` +
      `replaced by ${describeMatter(successor)} — target that instead.`
    );
  }

  const alternatives = getAssessableLeafMattersUnder(matterId);
  if (alternatives.length === 0) {
    return (
      `${matterId} is not an assessable sustainability matter, so it cannot carry a tailoring. ` +
      'Only the leaf matters of the ESRS hierarchy and the Upright entity-specific matters can.'
    );
  }

  const shown = alternatives.slice(0, MAX_SUGGESTED_MATTERS).map(describeMatter).join(', ');
  const omitted = alternatives.length - MAX_SUGGESTED_MATTERS;
  return (
    `${matterId} is a topic heading, not an assessable sustainability matter, so it cannot carry a ` +
    `tailoring. Target one of the assessable matters under it instead: ${shown}` +
    (omitted > 0 ? `, and ${omitted} more.` : '.')
  );
}

/** Rejects a `matterId` no DMA surface would render — see {@link nonAssessableMatterIdMessage}. */
const matterIdValidator = z.nativeEnum(SustainabilityMatterId).superRefine((matterId, ctx) => {
  const message = nonAssessableMatterIdMessage(matterId);
  if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, message });
});

const zTailoringBaseSchema = z.object({
  valence: z.nativeEnum(MaterialityValence),
  matterId: matterIdValidator,
  reasoning: reasoningValidator,
});

export const zRemoveFinancialTailoringSchema = zTailoringBaseSchema.extend({
  type: z.literal(TailoringType.REMOVE_FINANCIAL),
  materialityType: z.literal(MaterialityType.FINANCIAL),
  driver: z.nativeEnum(FinancialDriver),
  financialItem: z.nativeEnum(FinancialItem),
  riskOrOpportunityDescription: z.string(),
});

export const zRemoveImpactTailoringSchema = zTailoringBaseSchema.extend({
  type: z.literal(TailoringType.REMOVE_IMPACT),
  materialityType: z.literal(MaterialityType.IMPACT),
  /**
   * References the removed impact's description. Kept for the activity log and as a
   * fallback for matching legacy rows that don't carry `impactTopicId`.
   */
  impactDescription: z.string(),
  /**
   * Stable identifier of the impact topic the user disabled. Optional for backward
   * compatibility with rows created before PLT-4614; new UI submissions always include
   * it when the disabled impact has an `impactTopicId`. The matcher prefers this over
   * the description-based fallback.
   */
  impactTopicId: z.string().optional(),
});

const zRemoveTailoringSchema = z.union([zRemoveImpactTailoringSchema, zRemoveFinancialTailoringSchema]);

export const zPatchFinancialTailoringSchema = zTailoringBaseSchema.extend({
  type: z.literal(TailoringType.PATCH_FINANCIAL),
  description: descriptionValidator,
  materialityType: z.literal(MaterialityType.FINANCIAL),
  probability: z.nativeEnum(FinancialMaterialityLevel),
  magnitude: z.nativeEnum(FinancialMaterialityLevel),
  timeHorizon: z.nativeEnum(MaterialityTimeHorizon),
  driver: z.nativeEnum(FinancialDriver),
  financialItem: z.nativeEnum(FinancialItem),
});

const zPatchFinancialTailoringSchemaRefined = zPatchFinancialTailoringSchema.refine(
  data => {
    const validFinancialItemsForDriver = driverToValidFinancialItems[data.driver];
    return validFinancialItemsForDriver.includes(data.financialItem);
  },
  {
    message: 'Invalid financial item for driver',
    path: ['financialItem'],
  },
);

const zExclusiveMagnitudeSchema = z.union([
  z.object({
    magnitudeLevel: z.undefined().nullable(),
    magnitudeValue: z.number().min(0).max(1).positive(),
  }),
  z.object({
    magnitudeValue: z.undefined().nullable(),
    magnitudeLevel: z.nativeEnum(FinancialMaterialityLevel),
  }),
]);

const zExclusiveProbabilitySchema = z.union([
  z.object({ probabilityLevel: z.nativeEnum(FinancialMaterialityLevel), probabilityValue: z.undefined() }),
  z.object({ probabilityLevel: z.undefined(), probabilityValue: z.number().min(0).max(1).positive() }),
]);

const zExclusiveMagnitudeProbabilitySchema = z.intersection(zExclusiveMagnitudeSchema, zExclusiveProbabilitySchema);

const zPatchFinancialEffectsTailoringBaseSchema = zTailoringBaseSchema.extend({
  type: z.literal(TailoringType.PATCH_FINANCIAL_EFFECT),
  description: descriptionValidator,
  materialityType: z.literal(MaterialityType.FINANCIAL),
  timeHorizon: z.nativeEnum(MaterialityTimeHorizon),
  driver: z.nativeEnum(FinancialDriver),
  financialItem: z.nativeEnum(FinancialItem),
  ratioOfItemAffected: z
    .number()
    .min(0, { message: 'Share must be greater than 0%' })
    .max(1, { message: 'Share must be less than or equal to 100%' })
    .optional(),
});

export const zPatchFinancialEffectTailoringSchema = z
  .intersection(zPatchFinancialEffectsTailoringBaseSchema, zExclusiveMagnitudeProbabilitySchema)
  .refine(
    data => {
      const validFinancialItemsForDriver = driverToValidFinancialItems[data.driver];
      return validFinancialItemsForDriver.includes(data.financialItem);
    },
    {
      message: 'Invalid financial item for driver',
      path: ['financialItem'],
    },
  );

/**
 * Flat schema to allow validating form values single field at a time
 */
export const zPatchFinancialEffectsTailoringSchemaFlat = zPatchFinancialEffectsTailoringBaseSchema.extend({
  probabilityLevel: z.nativeEnum(FinancialMaterialityLevel).optional(),
  probabilityValue: z
    .number()
    .min(0, { message: 'Probability must be greater than 0%' })
    .max(1, { message: 'Probability must be less than or equal to 100%' })
    .positive({ message: 'Probability must be greater than 0%' })
    .optional(),
  magnitudeValue: z
    .number()
    .min(0, { message: 'Magnitude must be greater than 0%' })
    .max(1, { message: 'Magnitude must be less than or equal to 100%' })
    .positive({ message: 'Magnitude must be greater than 0%' })
    .optional(),
  magnitudeLevel: z.nativeEnum(FinancialMaterialityLevel).optional(),
});

const zPatchImpactTailoringBaseSchema = zTailoringBaseSchema.extend({
  type: z.literal(TailoringType.PATCH_IMPACT),
  description: descriptionValidator,
  materialityType: z.literal(MaterialityType.IMPACT),
  scaleLevel: z.nativeEnum(GenericLevel),
  probabilityLevel: z.nativeEnum(GenericLevel),
  scopeLevel: z.nativeEnum(GenericLevel),
  valueChain: z.nativeEnum(MaterialityValueChain),
  timeHorizon: z.nativeEnum(MaterialityTimeHorizon),
  actualness: z.nativeEnum(MaterialityActualness),
});

export const zNegativeValencePatchImpactTailoringSchema = zPatchImpactTailoringBaseSchema.extend({
  valence: z.literal(MaterialityValence.NEGATIVE),
  irremediabilityLevel: z.nativeEnum(GenericLevel),
});

export const zPositiveValencePatchImpactTailoringSchema = zPatchImpactTailoringBaseSchema.extend({
  valence: z.literal(MaterialityValence.POSITIVE),
});

const impactThresholdValidator = z.number().max(impactMinMaxThresholds.max).min(impactMinMaxThresholds.min);

export const zThresholdImpactTailoringSchema = z.object({
  type: z.literal(TailoringType.THRESHOLD_IMPACT),
  materialityType: z.literal(MaterialityType.IMPACT),
  reasoning: reasoningValidator,
  threshold: impactThresholdValidator,
});

const financialMaterialityThresholdValidator = z
  .number()
  .max(financialMinMaxThresholds.max)
  .min(financialMinMaxThresholds.min);

export const zThresholdFinancialTailoringSchema = z.object({
  type: z.literal(TailoringType.THRESHOLD_FINANCIAL),
  materialityType: z.literal(MaterialityType.FINANCIAL),
  reasoning: reasoningValidator,
  threshold: financialMaterialityThresholdValidator,
});

const financialEffectThresholdValidator = z
  .number()
  .max(financialEffectMinMaxThresholds.max)
  .min(financialEffectMinMaxThresholds.min);

export const zThresholdEffectTailoringSchema = z.object({
  type: z.literal(TailoringType.THRESHOLD_EFFECTS),
  materialityType: z.literal(MaterialityType.FINANCIAL),
  reasoning: reasoningValidator,
  threshold: financialEffectThresholdValidator,
});

export const zThresholdTailoringSchema = z.union([
  zThresholdImpactTailoringSchema,
  zThresholdFinancialTailoringSchema,
  zThresholdEffectTailoringSchema,
]);

export const zUpdateImpactThresholdTailoringSchema = z
  .object({
    reasoning: reasoningValidator,
    threshold: impactThresholdValidator,
  })
  .partial()
  .strict();

export const zUpdateFinancialThresholdTailoringSchema = z
  .object({
    reasoning: reasoningValidator,
    threshold: financialMaterialityThresholdValidator,
  })
  .partial()
  .strict();

export const zUpdateFinancialEffectThresholdTailoringSchema = z
  .object({
    reasoning: reasoningValidator,
    threshold: financialEffectThresholdValidator,
  })
  .partial()
  .strict();

export const zUpdateThresholdTailoringSchema = z.union([
  zUpdateImpactThresholdTailoringSchema,
  zUpdateFinancialThresholdTailoringSchema,
  zUpdateFinancialEffectThresholdTailoringSchema,
]);

export const zPatchImpactTailoringSchema = z.union([
  zNegativeValencePatchImpactTailoringSchema,
  zPositiveValencePatchImpactTailoringSchema,
]);

export const zPatchTailoringSchema = z.union([
  zPatchFinancialTailoringSchemaRefined,
  zPatchImpactTailoringSchema,
  zPatchFinancialEffectTailoringSchema,
]);

export const zCsrdTailoringSchema = z.union([zRemoveTailoringSchema, zPatchTailoringSchema, zThresholdTailoringSchema]);

export const zSupersedeTailoringSchema = z.object({
  removeTailoring: zRemoveTailoringSchema,
  patchTailoring: zPatchTailoringSchema,
});

/**
 * Only related to updating an existing tailoring
 */

const zUpdateTailoringBaseSchema = z
  .object({
    reasoning: z.string().min(1),
  })
  .partial()
  .strict();

const zUpdateRemoveTailoringSchema = zUpdateTailoringBaseSchema;

const assertDriverItemUpdateIsValid = (data: UpdatePatchFinancialTailoring | UpdatePatchFinancialEffectTailoring) => {
  if (!data.driver && !data.financialItem) {
    return true;
  }
  // If both fields are present, validate their relationship
  if (data.driver && data.financialItem) {
    const validItems = driverToValidFinancialItems[data.driver];
    return validItems.includes(data.financialItem);
  }
  // One needs to give both fields when updating so that we can validate the changes using zod. Another option would be to validate the changed driver + old item
  // combination with custom logic.
  return false;
};

const incorrectDriverItemMessage = {
  message: 'Selected financial item is not valid for the chosen driver',
  path: ['financialItem'], // Highlight the issue on the financialItem field
};

export const zUpdatePatchFinancialTailoringSchema = zUpdateTailoringBaseSchema
  .extend({
    probability: z.nativeEnum(FinancialMaterialityLevel),
    magnitude: z.nativeEnum(FinancialMaterialityLevel),
    timeHorizon: z.nativeEnum(MaterialityTimeHorizon),
    financialItem: z.nativeEnum(FinancialItem), // OH TODO: Validate that the selected combination is allowed
    driver: z.nativeEnum(FinancialDriver),
  })
  .partial()
  .strict()
  .refine(assertDriverItemUpdateIsValid, incorrectDriverItemMessage);

// OH: Note that probabilityValue and probabilityLevel are exclusive similarly to magnitudeValue and magnitudeLevel.
// This schema doesn't reflect that because it is used for flat validation of fields.
export const zUpdatePatchFinancialEffectTailoringSchema = zUpdateTailoringBaseSchema
  .extend({
    timeHorizon: z.nativeEnum(MaterialityTimeHorizon),
    driver: z.nativeEnum(FinancialDriver),
    financialItem: z.nativeEnum(FinancialItem),
    ratioOfItemAffected: z
      .number()
      .min(0, { message: 'Share must be greater than 0%' })
      .max(1, { message: 'Share must be less than or equal to 100%' })
      .optional(),
    probabilityLevel: z.nativeEnum(FinancialMaterialityLevel).nullable(),
    probabilityValue: z
      .number()
      .min(0, { message: 'Probability must be greater than 0%' })
      .max(1, { message: 'Probability must be less than or equal to 100%' })
      .positive({ message: 'Probability must be greater than 0%' })
      .nullable(),
    magnitudeValue: z
      .number()
      .min(0, { message: 'Magnitude must be greater than 0%' })
      .max(1, { message: 'Magnitude must be less than or equal to 100%' })
      .positive({ message: 'Magnitude must be greater than 0%' })
      .nullable(),
    magnitudeLevel: z.nativeEnum(FinancialMaterialityLevel).nullable(),
  })
  .partial()
  .strict()
  .refine(assertDriverItemUpdateIsValid, incorrectDriverItemMessage);

const zUpdatePatchImpactTailoringBaseSchema = zUpdateTailoringBaseSchema
  .extend({
    scaleLevel: z.nativeEnum(GenericLevel),
    probabilityLevel: z.nativeEnum(GenericLevel),
    scopeLevel: z.nativeEnum(GenericLevel),
    valueChain: z.nativeEnum(MaterialityValueChain),
    timeHorizon: z.nativeEnum(MaterialityTimeHorizon),
    actualness: z.nativeEnum(MaterialityActualness),
  })
  .partial()
  .strict();

export const zUpdateNegativeValencePatchImpactTailoringSchema = zUpdatePatchImpactTailoringBaseSchema
  .extend({
    irremediabilityLevel: z.nativeEnum(GenericLevel),
  })
  .partial()
  .strict();

export const zUpdatePositiveValencePatchImpactTailoringSchema = zUpdatePatchImpactTailoringBaseSchema;

const zUpdatePatchImpactTailoringSchema = z.union([
  zUpdateNegativeValencePatchImpactTailoringSchema,
  zUpdatePositiveValencePatchImpactTailoringSchema,
]);

const zUpdatePatchTailoringSchema = z.union([
  zUpdatePatchFinancialTailoringSchema,
  zUpdatePatchImpactTailoringSchema,
  zUpdatePatchFinancialEffectTailoringSchema,
]);

export const zUpdateTailoringSchema = z.union([
  zUpdateRemoveTailoringSchema,
  zUpdatePatchTailoringSchema,
  zUpdateThresholdTailoringSchema,
]);

/**
 * Agent-proposed tailoring batch apply (PLT-4895). The `propose_tailoring_batch` chat tool renders
 * a sign-off card; on approval the client POSTs the selected items here. The endpoint re-validates
 * each item's `tailoring` with `zCsrdTailoringSchema`, dedups against existing tailorings, creates
 * the rest in one transaction, recomputes once, and is idempotent on `proposalId`.
 */
export const MAX_TAILORING_BATCH_ITEMS = 70;

export type BatchApplyCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
};

/**
 * PLT-4960: supersede anchor. When present on a `patch-*` item, the apply step performs an atomic
 * "edit-as-duplicate": it disables the existing Upright IRO this anchor points at AND creates the
 * edited copy (the item's `tailoring`) in the same transaction. Mirrors the `existingIro` shape the
 * `propose_tailoring_batch` chat tool emits. The server looks the IRO up in the variant's stored
 * results by `clientId` on the patch's matter and valence, and builds the `remove-*` payload from
 * the stored IRO's description, impactTopicId, driver and financial item (PLT-7739) — the agent never
 * hands us a raw remove payload (option B). `description` is the label the card shows, and
 * `impactTopicId` only picks between stored impacts that share a `clientId`. Ignored on non-`patch-*`
 * items (a `remove-*` proposal already targets its IRO; thresholds carry no IRO).
 */
const zTailoringBatchSupersedeAnchorSchema = z.object({
  clientId: z.string().min(1).max(64),
  description: z.string().min(1).max(200),
  impactTopicId: z.string().max(64).optional(),
});

const zTailoringBatchItemSchema = z.object({
  // Stable id from the proposal item; echoed back in `createdItemIds`, `updatedItemIds`, `skippedItemIds` or
  // `rejectedItems`.
  itemId: z.string().min(1).max(64),
  tailoring: zCsrdTailoringSchema,
  existingIro: zTailoringBatchSupersedeAnchorSchema.optional(),
});

export const zTailoringBatchApplyBodySchema = z.object({
  proposalId: z.string().min(1).max(64),
  items: z.array(zTailoringBatchItemSchema).min(1).max(MAX_TAILORING_BATCH_ITEMS),
});

export type TailoringBatchSupersedeAnchor = z.infer<typeof zTailoringBatchSupersedeAnchorSchema>;
export type TailoringBatchApplyItem = z.infer<typeof zTailoringBatchItemSchema>;
export type TailoringBatchApplyBody = z.infer<typeof zTailoringBatchApplyBodySchema>;

/**
 * PLT-7535: why the apply refused a supersede item. Its anchor is already disabled by an existing
 * tailoring, is itself an edited copy (a `remove-*` never matches a tailored IRO at compute time),
 * or was superseded by an earlier item in the same batch. Applying any of them would leave a second
 * edited copy of the IRO next to the first.
 *
 * PLT-7739: `anchor-not-found` means no IRO in the stored results has the anchor's `clientId`, so a
 * disable would match nothing and the edited copy would stand next to the original.
 *
 * PLT-7767: `edit-matches-other-tailoring` means the edit has the dedup key of an existing tailoring
 * that neither is the anchor's edited copy nor replaces the anchor, so applying it would change that
 * tailoring and leave the anchor as it is.
 */
export type TailoringBatchRejectionReason =
  | 'anchor-already-disabled'
  | 'anchor-is-edited-copy'
  | 'anchor-superseded-earlier-in-batch'
  | 'anchor-not-found'
  | 'edit-matches-other-tailoring';

export type TailoringBatchRejectedItem = {
  itemId: string;
  reason: TailoringBatchRejectionReason;
  // Plain-language explanation, shown on the card receipt and relayed to the chat model.
  message: string;
  // The existing (or earlier-in-batch) tailoring the anchor conflicts with; for
  // `edit-matches-other-tailoring`, the tailoring the edit would have changed. For support, not shown.
  // Absent for `anchor-not-found`.
  conflictingTailoringId?: number;
};

/**
 * PLT-7740: why the apply skipped an item without changing anything.
 *
 * - `already-present`: a tailoring of the same IRO (see `dedupKeyFromTailoring`) already has the
 *   item's values. Only the reasoning may differ.
 * - `duplicate-in-batch`: an earlier item of the same batch already changes that IRO.
 */
export type TailoringBatchSkipReason = 'already-present' | 'duplicate-in-batch';

export type TailoringBatchSkippedItem = {
  itemId: string;
  reason: TailoringBatchSkipReason;
  // Plain-language explanation, shown on the card receipt and relayed to the chat model.
  message: string;
};

export type TailoringBatchApplyResult = {
  tailoredVariantId: string;
  createdTailoringIds: number[];
  skippedItemIds: string[];
  // PLT-7535: ids of the items whose tailoring was created, and the supersede items the apply
  // refused. Both are absent on results stored before these fields existed.
  createdItemIds?: string[];
  rejectedItems?: TailoringBatchRejectedItem[];
  // PLT-7740: the items whose IRO already had a tailoring of the same type with other values, and
  // the ids of those tailorings. The apply replaced the tailoring's values with the item's (e.g. a
  // new magnitude on an edited risk) instead of adding a second tailoring.
  updatedItemIds?: string[];
  updatedTailoringIds?: number[];
  // PLT-7740: why each of `skippedItemIds` was skipped.
  // The three PLT-7740 fields are absent on results stored before they existed.
  skippedItems?: TailoringBatchSkippedItem[];
  // Job id of the async CSRD recompute queued by the apply. Poll `/job/{id}/status` to know
  // when the recomputed DMA numbers have landed. Undefined when nothing was created or updated
  // (all items skipped or refused) or on results stored before this field existed. Persisted in
  // the idempotency row, so an `alreadyApplied` replay still carries the original job id.
  recomputeJobId?: number;
};

export type BatchApplyCsrdResultTailoringResponse = TailoringBatchApplyResult & {
  // True when this proposalId was already applied and the stored result was returned (idempotent re-POST).
  alreadyApplied?: boolean;
};

/**
 * Batch REMOVAL of existing result tailorings — the undo counterpart to the batch apply above,
 * signed off by a human on the `<TailoringRemovalCard>` in /chat (PLT-6322).
 *
 * Unlike the apply, this carries no `proposalId` and needs no idempotency table: removal is
 * naturally idempotent. A replayed POST finds the rows already gone, reports them in
 * `missingTailoringIds`, and queues no recompute — the recompute is queued only when at least
 * one row was actually destroyed.
 */
export type BatchRemoveCsrdResultTailoringRequestParams = {
  companyId: string;
  variantId: string;
};

export const zTailoringBatchRemoveBodySchema = z.object({
  tailoringIds: z.array(z.number().int().positive()).min(1).max(MAX_TAILORING_BATCH_ITEMS),
});

export type TailoringBatchRemoveBody = z.infer<typeof zTailoringBatchRemoveBodySchema>;

export type BatchRemoveCsrdResultTailoringResponse = {
  tailoredVariantId: string;
  removedTailoringIds: number[];
  /**
   * Requested ids that were already gone — a replayed POST, or a card rendered before someone
   * removed the same tailoring in the platform UI. Not an error: the end state the user asked
   * for is the state they got.
   */
  missingTailoringIds: number[];
  /** Job id of the async CSRD recompute. Absent when nothing was removed. */
  recomputeJobId?: number;
};

export type GetCsrdMaterialityThresholdsRequestParams = {
  variantId: string;
};

export type GetCsrdMaterialityThresholdsResponse = {
  [MaterialityType.IMPACT]: {
    defaultThreshold: number;
    threshold: number;
    tailoringId: number | undefined;
    min: number;
    max: number;
  };
  [MaterialityType.FINANCIAL]: {
    defaultThreshold: number;
    threshold: number;
    tailoringId: number | undefined;
    hasFe: boolean;
    min: number;
    max: number;
  };
};

export type UpdateThresholdTailoring = z.infer<typeof zUpdateThresholdTailoringSchema>;

/**
 * PLT-7869: turns own insights on for a custom company an investor assesses, the user's own request for what
 * the team does from the company's CSRD tailoring config page: a tailored version of the company's assessment,
 * and read and edit access to its tailorings for the organization.
 */
export type StartOwnInsightsRequestParams = {
  companyId: string;
};

export type StartOwnInsightsResponse = {
  originalVariantId: string;
  tailoringVariantId: string;
  /**
   * The computation of the tailored version, which the page waits for before showing it. Absent when the company
   * already had one, so there was nothing to start.
   */
  computeJobId?: number;
};
